import { AnimatePresence } from 'motion/react'
import React from 'react'
import styled from 'styled-components'

import { CloseIcon } from '@blade/ui-kit'

import { AnimateHeight } from '../animations'

import { SnackbarIcon } from './snackbar-icon'

import type { SnackbarIconProps } from './snackbar-icon'
import type { ClassNameProps } from '@root/shared/react'

export type SnackbarIntent = 'warning' | 'success' | 'error' | 'info'

export type SnackbarProps = {
  intent: SnackbarIntent
  description?:
    | React.ReactElement
    | string
    | ((props: { onClose?: () => void }) => React.ReactNode)
  action?: React.ReactNode
  onClose?: () => void
  icon?: SnackbarIconProps['icon']
} & React.PropsWithChildren &
  ClassNameProps

export const Snackbar = ({
  children,
  intent,
  description,
  onClose,
  icon,
  className,
}: SnackbarProps) => {
  return (
    <Root className={className}>
      <Header>
        <AnimatePresence mode="wait" initial={false}>
          <SnackbarIcon key={intent} intent={intent} icon={icon ?? intent} />
        </AnimatePresence>
        <Title>{children}</Title>
      </Header>

      {description ? (
        <AnimateHeight>
          <Description>
            {typeof description === 'function'
              ? description({ onClose })
              : description}
          </Description>
        </AnimateHeight>
      ) : undefined}

      {onClose ? (
        <Close type="button" onClick={onClose}>
          <CloseIcon size={16} />
        </Close>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  padding-block: 12px;
  padding-inline: 12px 24px;

  border-radius: 12px;
  background: var(--backgroundA-3);
  backdrop-filter: blur(11px);
  position: relative;
  pointer-events: initial;

  box-shadow: 0px 1px 20px 5px var(--appearanceA-3);
`

const Title = styled.div`
  font-weight: 600;
  font-size: 12px;
  line-height: normal;
  color: var(--text-12);
  grid-column: 2;
`

const Header = styled.div`
  display: grid;
  align-items: center;
  justify-content: start;
  gap: 10px;

  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
`

const Close = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  place-items: center;
  position: absolute;
  right: 8px;
  top: 8px;
  color: var(--textA-8);
`

const Description = styled.div`
  margin-top: 8px;
  color: var(--textA-10);
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
`
